import { useState, useEffect } from "react";
import {
  MdDescription,
  MdEdit,
  MdDownload,
  MdDelete,
  MdCheckCircle,
  MdPending,
  MdThumbUp,
  MdThumbDown,
} from "react-icons/md";
import ActionMenu from "../components/common/ActionMenu";
import api from "../services/api";
import toast from "react-hot-toast";
import { format } from "date-fns";

const API_URL =
  process.env.NODE_ENV === "production"
    ? (process.env.REACT_APP_API_URL || window.location.origin)
    : "http://localhost:5000";
const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const COLORS = ["#6366f1", "#f59e0b", "#10b981", "#ef4444", "#3b82f6"];
const avatarColor = (name) =>
  COLORS[(name?.charCodeAt(0) || 0) % COLORS.length];
const initials = (name) =>
  name
    ?.split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
const fmtSize = (bytes) => {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const STATUS_CONFIG = {
  pending: {
    icon: <MdPending size={13} />,
    cls: "badge-review",
    label: "Pending",
  },
  reviewed: {
    icon: <MdCheckCircle size={13} />,
    cls: "badge-in-progress",
    label: "Reviewed",
  },
  approved: {
    icon: <MdThumbUp size={13} />,
    cls: "badge-done",
    label: "Approved",
  },
  rejected: {
    icon: <MdThumbDown size={13} />,
    cls: "badge-critical",
    label: "Rejected",
  },
};

const UserAvatar = ({ user, size = 32 }) => {
  const src = user?.avatar
    ? user.avatar.startsWith("http")
      ? user.avatar
      : `${API_URL}${user.avatar}`
    : null;
  return (
    <div
      className="avatar"
      style={{
        width: size,
        height: size,
        fontSize: size * 0.3,
        background: src ? "transparent" : avatarColor(user?.name),
        flexShrink: 0,
      }}
    >
      {src ? (
        <img
          src={src}
          alt={user.name}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            borderRadius: "50%",
          }}
          onError={(e) => {
            e.target.style.display = "none";
            e.target.parentNode.textContent = initials(user?.name);
          }}
        />
      ) : (
        initials(user?.name)
      )}
    </div>
  );
};

// ─── Review Modal ─────────────────────────────────────────────
const ReviewModal = ({ sheet, onClose, onSaved }) => {
  const [status, setStatus] = useState(sheet.status);
  const [adminNote, setNote] = useState(sheet.adminNote || "");
  const [loading, setLoading] = useState(false);

  const handleSave = async () => {
    setLoading(true);
    try {
      await api.put(`/enrollments/${sheet._id}/status`, { status, adminNote });
      toast.success("Status updated");
      onSaved();
      onClose();
    } catch {
      toast.error("Failed to update");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal"
        style={{ maxWidth: 420 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h3>Review Sheet</h3>
          <button className="btn-icon" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="modal-body">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              marginBottom: 18,
              padding: "10px 14px",
              background: "var(--surface2)",
              borderRadius: 10,
            }}
          >
            <MdDescription size={22} color="var(--primary)" />
            <div>
              <div style={{ fontWeight: 700, fontSize: "0.875rem" }}>
                {sheet.originalName}
              </div>
              <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                {MONTHS[sheet.month - 1]} {sheet.year} — {sheet.user?.name}
              </div>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Status</label>
            <select
              className="form-control"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="pending">Pending</option>
              <option value="reviewed">Reviewed</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Admin Note</label>
            <textarea
              className="form-control"
              rows={3}
              value={adminNote}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Optional feedback..."
            />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={loading}
          >
            {loading ? "Saving..." : "Save Review"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────
const AdminEnrollments = () => {
  const [sheets, setSheets] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewSheet, setReviewSheet] = useState(null);
  const [, setDownloading] = useState(null);
  const [filterUser, setFilterUser] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterMonth, setFilterMonth] = useState("");

  const fetchAll = async () => {
    const params = {};
    if (filterUser) params.userId = filterUser;
    if (filterStatus) params.status = filterStatus;
    if (filterMonth) params.month = filterMonth;
    const [s, u] = await Promise.all([
      api.get("/enrollments", { params }),
      api.get("/users"),
    ]);
    setSheets(s.data.data);
    setUsers(u.data.data);
    setLoading(false);
  };
  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterUser, filterStatus, filterMonth]);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this sheet?")) return;
    await api.delete(`/enrollments/${id}`);
    toast.success("Deleted");
    fetchAll();
  };

  const handleDownload = async (sheet) => {
    setDownloading(sheet._id);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(
        `${API_URL}/api/enrollments/${sheet._id}/download`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Download failed");
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = sheet.originalName;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Downloaded!");
    } catch (err) {
      toast.error(err.message || "Download failed");
    } finally {
      setDownloading(null);
    }
  };

  const counts = {
    total: sheets.length,
    pending: sheets.filter((s) => s.status === "pending").length,
    approved: sheets.filter((s) => s.status === "approved").length,
    rejected: sheets.filter((s) => s.status === "rejected").length,
  };

  if (loading)
    return (
      <div className="loading">
        <div className="spinner" />
      </div>
    );

  return (
    <main className="page">
      {reviewSheet && (
        <ReviewModal
          sheet={reviewSheet}
          onClose={() => setReviewSheet(null)}
          onSaved={fetchAll}
        />
      )}

      <div className="page-header">
        <div>
          <h1 className="page-title">All Enrollments</h1>
          <p className="page-subtitle">
            Review and manage all staff enrollment sheets
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-4" style={{ marginBottom: 24 }}>
        {[
          {
            label: "Total Sheets",
            value: counts.total,
            color: "#6366f1",
            bg: "#e0e7ff",
            icon: <MdDescription />,
          },
          {
            label: "Pending",
            value: counts.pending,
            color: "#f59e0b",
            bg: "#fef3c7",
            icon: <MdPending />,
          },
          {
            label: "Approved",
            value: counts.approved,
            color: "#10b981",
            bg: "#d1fae5",
            icon: <MdThumbUp />,
          },
          {
            label: "Rejected",
            value: counts.rejected,
            color: "#ef4444",
            bg: "#fee2e2",
            icon: <MdThumbDown />,
          },
        ].map((s) => (
          <div key={s.label} className="card stat-card">
            <div className="stat-icon" style={{ background: s.bg }}>
              <span style={{ color: s.color, fontSize: 22, display: "flex" }}>
                {s.icon}
              </span>
            </div>
            <div className="stat-value">{s.value}</div>
            <div className="stat-label">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div
        style={{
          display: "flex",
          gap: 10,
          marginBottom: 20,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <select
          className="form-control"
          style={{ width: "auto" }}
          value={filterUser}
          onChange={(e) => setFilterUser(e.target.value)}
        >
          <option value="">All Staff</option>
          {users.map((u) => (
            <option key={u._id} value={u._id}>
              {u.name}
            </option>
          ))}
        </select>
        <select
          className="form-control"
          style={{ width: "auto" }}
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
        >
          <option value="">All Statuses</option>
          <option value="pending">Pending</option>
          <option value="reviewed">Reviewed</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
        <select
          className="form-control"
          style={{ width: "auto" }}
          value={filterMonth}
          onChange={(e) => setFilterMonth(e.target.value)}
        >
          <option value="">All Months</option>
          {MONTHS.map((m, i) => (
            <option key={m} value={String(i + 1)}>
              {m}
            </option>
          ))}
        </select>
        {(filterUser || filterStatus || filterMonth) && (
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => {
              setFilterUser("");
              setFilterStatus("");
              setFilterMonth("");
            }}
          >
            Clear
          </button>
        )}
        <span
          className="text-muted"
          style={{ marginLeft: "auto", fontSize: "0.82rem" }}
        >
          {sheets.length} sheet{sheets.length !== 1 ? "s" : ""}
        </span>
      </div>

      <div className="card">
        {sheets.length === 0 ? (
          <div className="empty-state">
            <MdDescription size={52} />
            <h3>No sheets found</h3>
            <p>No enrollment sheets match your filters.</p>
          </div>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Staff Member</th>
                  <th>Period</th>
                  <th>File</th>
                  <th>Size</th>
                  <th>Note</th>
                  <th>Status</th>
                  <th>Admin Note</th>
                  <th>Submitted</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sheets.map((s) => {
                  const sc = STATUS_CONFIG[s.status] || STATUS_CONFIG.pending;
                  return (
                    <tr key={s._id}>
                      <td>
                        {s.user && (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 10,
                            }}
                          >
                            <UserAvatar user={s.user} size={34} />
                            <div>
                              <div
                                style={{
                                  fontWeight: 700,
                                  fontSize: "0.875rem",
                                }}
                              >
                                {s.user.name}
                              </div>
                              <div
                                className="text-muted"
                                style={{ fontSize: "0.72rem" }}
                              >
                                {s.user.email}
                              </div>
                            </div>
                          </div>
                        )}
                      </td>
                      <td>
                        <div style={{ fontWeight: 700 }}>
                          {MONTHS[s.month - 1]} {s.year}
                        </div>
                      </td>
                      <td>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                          }}
                        >
                          <div
                            style={{
                              width: 30,
                              height: 30,
                              borderRadius: 8,
                              background: "var(--primary-light)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                            }}
                          >
                            <MdDescription size={15} color="var(--primary)" />
                          </div>
                          <div
                            style={{
                              fontWeight: 600,
                              fontSize: "0.8rem",
                              maxWidth: 160,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {s.originalName}
                          </div>
                        </div>
                      </td>
                      <td
                        className="text-muted"
                        style={{ fontSize: "0.82rem" }}
                      >
                        {fmtSize(s.fileSize)}
                      </td>
                      <td
                        className="text-muted"
                        style={{ fontSize: "0.82rem", maxWidth: 120 }}
                      >
                        {s.note || "—"}
                      </td>
                      <td>
                        <span className={`badge ${sc.cls}`} style={{ gap: 4 }}>
                          {sc.icon}
                          {sc.label}
                        </span>
                      </td>
                      <td
                        className="text-muted"
                        style={{ fontSize: "0.82rem", maxWidth: 140 }}
                      >
                        {s.adminNote || "—"}
                      </td>
                      <td
                        className="text-muted"
                        style={{ fontSize: "0.78rem", whiteSpace: "nowrap" }}
                      >
                        {format(new Date(s.createdAt), "MMM d, yyyy")}
                      </td>
                      <td>
                        <div
                          style={{ display: "flex", gap: 4, flexWrap: "wrap" }}
                        >
                          <ActionMenu
                            items={[
                              {
                                label: "Review Sheet",
                                icon: <MdEdit size={15} />,
                                onClick: () => setReviewSheet(s),
                              },
                              {
                                label: "Download File",
                                icon: <MdDownload size={15} />,
                                onClick: () => handleDownload(s),
                              },
                              "divider",
                              {
                                label: "Delete Sheet",
                                icon: <MdDelete size={15} />,
                                onClick: () => handleDelete(s._id),
                                danger: true,
                              },
                            ]}
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </main>
  );
};

export default AdminEnrollments;
